import publicMediaConfig from '@/data/publicMediaConfig.json';

const ABSOLUTE_URL_PATTERN = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i;

function decodePathComponent(value) {
    try {
        return decodeURIComponent(value);
    } catch (error) {
        return value;
    }
}

function normalizePathComponent(value) {
    // encodeURIComponent leaves apostrophes untouched, but an apostrophe can
    // terminate a quoted CSS url('...') value. Encode it explicitly so media
    // URLs are safe in both regular URL attributes and CSS backgrounds.
    return encodeURIComponent(decodePathComponent(value)).replace(/'/g, '%27');
}

function normalizePathPreservingSeparators(pathValue) {
    // Normalize each segment independently so encoded slashes are not introduced.
    return pathValue
        .split('/')
        .map(segment => (segment ? normalizePathComponent(segment) : segment))
        .join('/');
}

function stripLeadingMediaRoot(pathValue, mediaType) {
    const mediaRoot = mediaType === 'photos' ? 'photos' : 'documents';

    return String(pathValue || '')
        .replace(/^\/+/, '')
        .replace(new RegExp(`^${mediaRoot}/+`, 'i'), '');
}

function joinMediaBasePath(basePath, pathValue) {
    return `${String(basePath).replace(/\/+$/, '')}/${pathValue}`;
}

export function normalizeMediaUrl(value) {
    if (!value) return '';

    const trimmedValue = String(value).trim();
    if (!trimmedValue) return '';

    if (/^(?:data:|blob:)/i.test(trimmedValue)) {
        return trimmedValue;
    }

    if (ABSOLUTE_URL_PATTERN.test(trimmedValue)) {
        try {
            // Parse absolute URLs so only pathname is normalized while host/query remain intact.
            const url = new URL(trimmedValue, 'http://localhost');
            url.pathname = normalizePathPreservingSeparators(url.pathname);
            return url.toString();
        } catch (error) {
            // If URL parsing fails, keep the original value to avoid breaking rendering.
            return trimmedValue;
        }
    }

    return normalizePathPreservingSeparators(trimmedValue);
}

export function normalizeLinkedPersonIDs(value) {
    if (!value) return [];

    if (Array.isArray(value)) {
        return value.filter(Boolean).map(id => String(id).trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
        return value
            .split(',')
            .map(id => id.trim())
            .filter(Boolean);
    }

    return [];
}

function inferOverlayMediaType(path) {
    const extension = String(path || '').split('.').pop().toLowerCase();

    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
        return 'image';
    }

    return 'document';
}

export function normalizeOverlayMediaKind(value) {
    const normalizedValue = String(value || '').trim().toLowerCase();

    if (normalizedValue === 'photo' || normalizedValue === 'photos') {
        return 'photo';
    }

    if (normalizedValue === 'document' || normalizedValue === 'documents') {
        return 'document';
    }

    return 'unknown';
}

export function normalizeOverlayMedia(media = {}, options = {}) {
    const source = media || {};
    const rawPath = options.path ?? source.url ?? source.path ?? '';
    const url = options.url ?? (
        typeof options.pathResolver === 'function'
            ? options.pathResolver(rawPath, source)
            : rawPath
    );

    return {
        url,
        type: options.type || source.type || inferOverlayMediaType(rawPath),
        kind: normalizeOverlayMediaKind(
            options.kind ?? source.kind ?? source.mediaKind ?? source.mediaType
        ),
        title: source.title || source.description || options.defaultTitle || '',
        owner: source.owner || source.mediaOwner || options.defaultOwner || '',
        linkedPersonIDs: normalizeLinkedPersonIDs(source.linkedPersonIDs)
    };
}

export function getBackgroundImageUrl(value) {
    if (!value) {
        return "url('path/to/default-thumbnail.jpg')";
    }

    const normalizedValue = normalizeMediaUrl(value)
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'");

    return `url('${normalizedValue}')`;
}

export function isPublicDocument(title) {
    if (!title) return false;
    const lowerCaseTitle = title.toLowerCase();
    // Keyword list is data-driven so legal/public policy changes can be updated
    // without touching application logic.
    return publicMediaConfig.publicKeywords.some(keyword =>
        lowerCaseTitle.includes(keyword.toLowerCase())
    );
}

/**
 * Get the full path for a media file (photo or document)
 * @param {string} path - The relative path of the media file
 * @param {string} mediaType - Type of media: 'photos' or 'documents' (default: 'documents')
 * @returns {string} Full URL to the media file
 */
export function getMediaPath(path, mediaType = 'documents') {
    if (!path) return 'path/to/default-file.jpg';

    const normalizedPath = normalizeMediaUrl(path);
    if (ABSOLUTE_URL_PATTERN.test(normalizedPath) || /^(?:data:|blob:)/i.test(normalizedPath)) {
        return normalizedPath;
    }

    const mediaPath = stripLeadingMediaRoot(normalizedPath, mediaType);
    const basePath = mediaType === 'photos'
        ? (process.env.VUE_APP_PHOTO_API_URL || 'http://localhost:8082/photos/')
        : (process.env.VUE_APP_DOCUMENTS_API_URL || 'http://localhost:8082/documents/');

    return joinMediaBasePath(basePath, mediaPath);
}

/**
 * Get the full path for a media thumbnail (photo or document)
 * @param {string} thumbpath - The relative path of the thumbnail
 * @param {string} mediaType - Type of media: 'photos' or 'documents' (default: 'documents')
 * @returns {string} Full URL to the thumbnail
 */
export function getMediaThumbnailPath(thumbpath, mediaType = 'documents') {
    if (!thumbpath) return 'path/to/default-thumbnail.jpg';

    const normalizedThumbpath = normalizeMediaUrl(thumbpath);
    if (ABSOLUTE_URL_PATTERN.test(normalizedThumbpath) || /^(?:data:|blob:)/i.test(normalizedThumbpath)) {
        return normalizedThumbpath;
    }

    const mediaThumbpath = stripLeadingMediaRoot(normalizedThumbpath, mediaType);
    const basePath = mediaType === 'photos'
        ? (process.env.VUE_APP_PHOTO_API_URL || 'http://localhost:8082/photos/')
        : (process.env.VUE_APP_DOCUMENTS_API_URL || 'http://localhost:8082/documents/');

    return joinMediaBasePath(basePath, mediaThumbpath);
}

/**
 * Convenience function for photo paths
 */
export function getPhotoPath(path) {
    return getMediaPath(path, 'photos');
}

/**
 * Convenience function for photo thumbnail paths
 */
export function getPhotoThumbnailPath(thumbpath) {
    return getMediaThumbnailPath(thumbpath, 'photos');
}

/**
 * Convenience function for document paths
 */
export function getDocumentPath(path) {
    return getMediaPath(path, 'documents');
}

/**
 * Convenience function for document thumbnail paths
 */
export function getDocumentThumbnailPath(thumbpath) {
    return getMediaThumbnailPath(thumbpath, 'documents');
}
